import {Avatar, Button, DatePicker, Input, Modal, Radio, Tag} from 'antd'
import {getId} from 'apis/model/base'
import {group_getMembers_Api, group_regenerateCode_Api, organization_getGroups_Api} from 'apis'
import useAsyncAction from '../../../../modules/asyncCache/useAsyncAction'
import useAsync from 'modules/asyncCache/useAsync'
import getTitle from 'helpers/getTitle'
import {RequiredSystemAdmin} from '../../../../components/RequiredAdmin'
import {ResetButton, SubmitButton} from 'components/form/FormActionBar'
import {ChipGroup, Field, Section} from 'components/form/ModernForm'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {Formik} from 'formik'
import _ from 'lodash'
import classNames from 'classnames'
import useAsyncWithCache from 'modules/asyncCache/useAsyncWithCache'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import {useOrgTypeLabel} from 'modules/local/useOrgTypeLabels'
import React, {useContext, useMemo} from 'react'
import {compose, mapProps, pure, withProps} from 'recompose'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {deepTranslate, getResponseItems, NullAsDefault, renderIf, shallowDiff} from 'views/Shared'
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import {BooleanPicker} from "../../Exam/ExamContext";
import {createSelectValue, createValue} from "../../../../components/form/utils";
import {StatusTypes} from "../../appFieldTypes";
import Translate from "../../../../modules/local/Translate";
import LookupField from "../../../../components/LookupField";
import SlateEditor from "../../../SlateEditor";
import {ModalEditorToolbar} from "../../../SlateEditor/SlateEditor";
import {convertFromString, convertToString} from "../../../SlateEditor/functions";
import {SelectWidget} from "../../custom/ReactSelectComponents";
import {CustomToggleField} from "../../../../components/form/fields/ToggleField";
import {BsChevronRight, BsGeoAlt, BsShieldFillExclamation} from "react-icons/bs";
import {SaintsOutlineSvg} from "../../../../components/Svgs/SaintsPlaceholder";
import dayjs from "dayjs";
import {IoCalendarOutline} from "react-icons/io5";
import {HiOutlineDocumentText} from "react-icons/hi2";
import {joinPolicyOptions, privacyOptions} from "../../../../conts/privacyOptions";
import {
    firstLetterUppercase,
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules
} from "../../../../components/form/ValidateForm";
import {RiInformationLine} from "react-icons/ri";
import {PiMusicNotesFill} from "react-icons/pi";

const JoinCodeField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const groupId = _.get(form, 'values.id')
    const {handleAsyncAction: doRegenerate, loading} = useAsyncAction({
        apiInfo: group_regenerateCode_Api,
        onSuccess: (result, data) => {
            const newCode = _.get(data, 'response.data.join_code')
            if (newCode) onChange(createValue(name, newCode))
        },
    })
    return (
        <div className="flex items-center gap-2">
            <Input value={value} disabled className="w-full font-mono" style={{letterSpacing: '0.1em'}}/>
            <Button
                loading={loading}
                disabled={!groupId}
                onClick={() => Modal.confirm({
                    title: t('regenerate code confirm'),
                    content: t('regenerate code confirm description'),
                    okText: t('regenerate'),
                    okType: 'danger',
                    cancelText: t('cancel'),
                    onOk: () => doRegenerate({}, {id: groupId}),
                })}>
                {t('regenerate')}
            </Button>
        </div>
    )
}

const ParentDepartmentField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const organization = _.get(form, 'values.organization')
    const org_id = getId(organization) || _.get(form, 'values.organization_id')
    const deptLabel = useOrgTypeLabel(organization?.org_type, 'group', organization?.label_privacy) || t('parent group / ministry')

    const {response, isLoading} = useAsyncWithCache({
        cacheId: [organization_getGroups_Api.path, org_id].join('/'),
        apiInfo: organization_getGroups_Api,
        query: {id: org_id},
        once: true,
    })

    const options = useMemo(() => {
        const items = getResponseItems(response) || []
        return items.map(dept => ({value: dept.id, label: dept.title}))
    }, [response])

    return (
        <div className="flex flex-col gap-1">
            <span className="font-medium text-xs text-color-300 uppercase tracking-wide">
                {deptLabel}
            </span>
            <SelectWidget
                name={name}
                allowClear
                loading={isLoading}
                className="w-full"
                value={value || undefined}
                options={options}
                placeholder={t('click to select')}
                onChange={(v) => onChange(createSelectValue(name, v))}
            />
        </div>
    )
}

// FormSection/TypeSpecificSection (HeaderWithIconLine, pattern cũ) đã bỏ - GroupCreateForm/
// GroupUpdateForm chuyển sang Section/Field của components/form/ModernForm.js (Modern Form Design
// Pattern, xem docs/ui/form-layout.md). Không còn nơi nào import 2 component này (đã kiểm tra toàn
// repo) nên xoá hẳn thay vì để lại dead code.

// Field riêng của choir - chọn ca trưởng từ danh sách thành viên hiện có (chỉ người đã liên kết
// tài khoản mới chọn được, vì conductor_id là FK tới users.id - xem GroupMemberTransformer,
// member chưa liên kết tài khoản không có user.id).
const ConductorField = ({name, value, onChange, form}) => {
    const t = useTranslate()
    const groupId = _.get(form, 'values.id')
    const initialConductor = _.get(form, 'values.conductor')

    const {response, isLoading} = useAsync({
        apiInfo: group_getMembers_Api,
        query: {':id': groupId},
    })

    const options = useMemo(() => {
        const memberOptions = getResponseItems(response)
            .filter((m) => !!_.get(m, 'user.id'))
            .map((m) => ({
                value: m.user.id,
                label: _.get(m, 'user.name') || _.get(m, 'user.label'),
            }))
        return initialConductor?.id && !memberOptions.some((o) => o.value === initialConductor.id)
            ? [{value: initialConductor.id, label: getTitle(initialConductor)}, ...memberOptions]
            : memberOptions
    }, [response, initialConductor])

    return (
        <SelectWidget
            name={name}
            allowClear
            showSearch
            loading={isLoading}
            className="w-full"
            value={value || undefined}
            options={options}
            placeholder={t('click to select')}
            onChange={(v) => onChange(createSelectValue(name, v))}
        />
    )
}

const conductorSchema = {
    title: 'conductor',
    children: {
        name: 'conductor_id',
        component: ConductorField,
    },
}

// group_type nào đã tự đủ nghĩa (1-1 với 1 category cố định) thì group_title (Phân loại) không cần
// chọn tay nữa - khoá cứng theo key dịch tương ứng, tránh 2 field lạc nhau (group_type là code,
// group_title là tên hiển thị suy ra TỪ code, không phải 1 nguồn nhập liệu độc lập). group_type nào
// còn nhiều category con (vd department: Giáo Khu/Hội đoàn/Phụng vụ...) thì để trống - vẫn cho chọn
// tay qua LookupField như cũ.
const FIXED_GROUP_TITLE_BY_TYPE = {
    choir: 'choir',
}

// Registry field chung cho mọi group_type - export để GroupChoirForm (và các GroupXxxForm khác sau
// này) tái dùng nguyên field definition thay vì viết lại, chỉ chọn section cần + thêm section riêng.
export const formSchema = {
    title: {
        title: 'group name',
        children: [
            {
                name: 'title',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, translate, onChange}) => ({
                        showCount: true,
                        maxLength: 120,
                        name,
                        value,
                        onChange,
                        placeholder: translate('title'),
                    })),
                    pure
                )(Input),
            },
        ],
    },
    description: {
        title: 'description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    showCount: true,
                    maxLength: 500,
                    rows: 6,
                    placeholder: 'description',
                    Component: Input.TextArea,
                }),
            },
        ],
    },

    active: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-3 gap-3',
        itemClassNames: ['col-span-1 md:col-span-2', 'col-span-1'],
        children: [
            {
                label: 'status',
                name: 'status',
                component: ({name, value, form, onChange}) => {
                    const options = Array.from(form?.values?.status_options ?? [])
                    return (
                        <Radio.Group
                            name={name}
                            value={value}
                            options={options}
                            onChange={onChange}
                            optionType="button"
                            buttonStyle="solid"
                            className="w-full custom-radio-button"
                        />
                    )
                },
            },
            {
                name: 'active',
                label: 'active',
                component: createControlledFormField({
                    Component: BooleanPicker,
                }),
            },
        ],
    },

    privacy: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                label: 'privacy',
                name: 'privacy',
                component: compose(
                    withTranslate,
                    mapProps(({name, translate, onChange, value}) => ({
                        name,
                        allowClear: true,
                        className: 'w-full',
                        value: value || undefined,
                        onChange: (value) => {
                            onChange(createSelectValue('privacy', value))
                        },
                        placeholder: translate('---Choose One---'),
                        options: Object.values(privacyOptions).map(deepTranslate(translate))
                    })),
                    pure
                )(SelectWidget),
            },
            {
                label: 'join policy',
                name: 'join_policy',
                component: compose(
                    withTranslate,
                    mapProps(({name, translate, onChange, value}) => ({
                        name,
                        allowClear: true,
                        className: 'w-full',
                        value: value || undefined,
                        onChange: (value) => {
                            onChange(createSelectValue('join_policy', value))
                        },
                        placeholder: translate('---Choose One---'),
                        options: Object.values(joinPolicyOptions).map(deepTranslate(translate))
                    })),
                    pure
                )(SelectWidget),
            },
        ]
    },
    channel_post_permission: {
        title: 'posting permission',
        children: {
            name: 'channel_post_permission',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 3,
                    onChange: (value) => {
                        onChange(createValue(name, Boolean(value) ? 3 : 2))
                    },
                    title: translate('posting permission'),
                    description: translate("Allow changing posting permissions on the news channel; On: only administrators are allowed to post, Off: all students are allowed to post"),
                    className: "p-2 border border-dashed border-secondary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },

    public: {
        children: {
            name: 'public',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 1,
                    onChange: (value) => {
                        onChange(createValue(name, value ? 1 : 0))
                    },
                    title: translate('show publicly'),
                    description: translate('allow others to find this group'),
                    className: "p-2 border border-dashed border-secondary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },
    public_by_server: {
        children: {
            name: 'public_by_server',
            component: compose(
                withTranslate,
                mapProps(({name, value, onChange, translate}) => ({
                    name,
                    value: Number(value) === 1,
                    onChange: (value) => {
                        onChange(createValue(name, value ? 1 : 0))
                    },
                    title: translate('show publicly (super admin)'),
                    description: translate('allow others to find this group'),
                    className: "p-2 border border-dashed border-secondary rounded-lg"
                }))
            )(CustomToggleField),
        },
    },

    group_type: {
        title: 'group type',
        children: {
            name: 'group_type',
            component: ({value}) => (
                <Translate>
                    {(t) => (
                        <Tag color={value === 'choir' ? 'purple' : 'default'} className="w-fit px-3 py-1 text-sm">
                            {t(value || 'department')}
                        </Tag>
                    )}
                </Translate>
            ),
        },
    },

    // Chọn group_type lúc tạo mới (department mặc định / choir) - khác với formSchema.group_type ở
    // trên (chỉ hiển thị readonly lúc update, không cho đổi type sau khi tạo). group_type nào có trong
    // FIXED_GROUP_TITLE_BY_TYPE (hiện: choir) sẽ tự điền + khoá "Phân loại" (group_title) theo đúng
    // key dịch tương ứng, để group tạo ra đồng bộ 100% với tính năng Ca đoàn (group_type=choir dùng
    // chung GroupChoir - xem Group::businessRelationByGroupType / syncBusinessDetail ở backend).
    create_group_type: {
        title: 'group type',
        children: {
            name: 'group_type',
            component: ({name, value, onChange, form}) => (
                <Translate>
                    {(t) => (
                        <Radio.Group
                            name={name}
                            value={value || 'department'}
                            optionType="button"
                            buttonStyle="solid"
                            className="w-full custom-radio-button"
                            options={[
                                {label: t('department'), value: 'department'},
                                {label: t('choir'), value: 'choir'},
                            ]}
                            onChange={(e) => {
                                const newType = e.target.value
                                onChange(createValue('group_type', newType))
                                const fixedTitleKey = FIXED_GROUP_TITLE_BY_TYPE[newType]
                                form.setFieldValue(
                                    'group_title',
                                    fixedTitleKey ? t(fixedTitleKey) : undefined
                                )
                            }}
                        />
                    )}
                </Translate>
            ),
        },
    },
    idname: {
        title: 'idname',
        children: {
            name: 'idname',
            component: ({value}) => <Input value={value} disabled className="w-full font-mono"/>,
        },
    },
    channel: {
        title: 'channel',
        children: {
            name: 'channel',
            component: ({value}) => (
                <Translate>
                    {(t) => <Input value={_.get(value, 'title') || t('no data')} disabled className="w-full"/>}
                </Translate>
            ),
        },
    },
    join_code: {
        title: 'join code',
        children: {
            name: 'join_code',
            component: JoinCodeField,
        },
    },

    parent_id: {
        children: {
            name: 'parent_id',
            component: ParentDepartmentField,
        },
    },

    group_name: {
        title: 'serving in',
        children: [
            {
                name: 'group_title',
                component: ({name, value, onChange, form}) => {
                    const values = _.get(form, 'values', {})
                    // group_type nào đã suy ra được group_title cố định (xem FIXED_GROUP_TITLE_BY_TYPE +
                    // create_group_type ở trên) thì ẩn hẳn LookupField, hiển thị thay bằng ô readonly -
                    // group_type là code nguồn sự thật, group_title chỉ là tên hiển thị suy ra theo code,
                    // không để user chọn lệch tay làm 2 field lạc nhau.
                    const fixedTitleKey = FIXED_GROUP_TITLE_BY_TYPE[values?.group_type]
                    if (fixedTitleKey) {
                        return (
                            <Translate>
                                {(t) => <Input value={t(fixedTitleKey)} disabled className="w-full"/>}
                            </Translate>
                        )
                    }
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={values?.group_title}
                                    apiPath={`/lookup/group_title/gets?group_type=group`}
                                    name={name}
                                    placeholder={t('group title placeholder')}
                                    handleChange={(e) => {
                                        onChange(
                                            createValue('group_title', e.target.value)
                                        )
                                    }}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => (
                                        <div key={i} className="flex items-center flex-1 gap-2 md:gap-3">
                                            <Avatar
                                                size={40}
                                                className="flex flex-center bg-secondary-100 rounded-xl"
                                                icon={<BsGeoAlt size={20} className="text-secondary"/>}
                                            />
                                            <div className="flex flex-col gap-1 flex-1">
                                                <span className="font-bold text-sm text-color-000 leading-snug">
                                                    {o.label}
                                                </span>
                                            </div>
                                            <BsChevronRight size={16} className="text-color-500"/>
                                        </div>
                                    )}
                                />
                            )}
                        </Translate>
                    )
                },
            },
        ],
    },
    about: {
        title: 'about',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },
    patron_saint: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                label: 'patron saint',
                name: 'patron_saint',
                component: ({name, value, onChange, form, ...restForm}) => {
                    return (
                        <Translate>
                            {(t) => (
                                <LookupField
                                    createIfNotExists={true}
                                    defaultValue={form?.values?.patron_saint_name}
                                    selectedOption={form?.values?.patron_saint}
                                    apiPath={`/lookup/saint/get-by-types`}
                                    name={name}
                                    OptionWrapper={({children, ...props}) =>
                                        <div className="lookup-item" {...props}>
                                            {children}
                                        </div>
                                    }
                                    renderOption={(o, i) => {
                                        return (
                                            <div key={i} className="flex items-start flex-1 gap-3">
                                                <Avatar
                                                    size={40}
                                                    src={o.avatar}
                                                    className="flex flex-center bg-blue-50 rounded-lg"
                                                    icon={<SaintsOutlineSvg size={20} className="text-blue-500"/>}
                                                />
                                                <div className="flex flex-col flex-1 max-lines-3">
                                                    <span
                                                        className="font-bold text-sm text-color-000 tracking-wide leading-tight">
                                                        {`${o.common_name || o.name} (${o.patron_saint_feast_day || o.feast_day})`}
                                                    </span>
                                                    {renderIf(
                                                        o?.description,
                                                        <div
                                                            className="text-xs text-color-400 italic max-lines-2 tracking-wider leading-snug">
                                                            {o?.description}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    }}
                                    handleChange={(e) => {
                                        form.setValues({
                                            ...form.values,
                                            patron_saint: e.target,
                                            patron_saint_name: e.target.common_name || e.target.value,
                                            patron_saint_feast_day: e.target.feast_day,
                                            patron_saint_id: e.target.id
                                        });
                                    }}
                                />
                            )}
                        </Translate>
                    )
                },
            },
            {
                label: 'patron saint feast day',
                name: 'patron_saint_feast_day',
                component: compose(
                    withTranslate,
                    withProps(({value, onChange, translate}) => ({
                        onChange: (date, dateString) => {
                            onChange(createValue('patron_saint_feast_day', dateString))
                        },
                        value: !!value ? dayjs(value, "DD/MM") : null,
                        className: 'w-full',
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        name: 'patron saint feast day',
                        placeholder: translate('patron saint feast day placeholder'),
                        format: 'DD/MM'
                    }))
                )(DatePicker),
            },
        ],
    },
}

// Tái dùng thẳng các field phức tạp đã có sẵn trong formSchema (LookupField "Phân loại"/"Thánh bảo
// trợ", DatePicker ngày kính, SlateEditor giới thiệu, Select tra cứu quản ca...) cho GroupCreateForm/
// GroupUpdateForm (Modern Form Design Pattern - Section/Field, xem docs/ui/form-layout.md) - không
// viết lại logic bên trong, chỉ đổi khung ngoài. title/description KHÔNG lấy qua registry (field đơn
// giản, cần placeholder đổi theo group_type - viết thẳng Input/TextArea trong JSX cho dễ).
const GroupTitleInput = formSchema.group_name.children[0].component
const PatronSaintInput = formSchema.patron_saint.children[0].component
const PatronSaintFeastDayInput = formSchema.patron_saint.children[1].component
const AboutInput = formSchema.about.children[0].component
const ActiveToggleInput = formSchema.active.children[1].component
const PrivacyInput = formSchema.privacy.children[0].component
const JoinPolicyInput = formSchema.privacy.children[1].component
const ChannelPostPermissionInput = formSchema.channel_post_permission.children.component
const PublicToggleInput = formSchema.public.children.component
const PublicByServerToggleInput = formSchema.public_by_server.children.component
const GroupTypeTagInput = formSchema.group_type.children.component
const IdnameInput = formSchema.idname.children.component
const ChannelInput = formSchema.channel.children.component
const JoinCodeInput = formSchema.join_code.children.component

// department/choir - 2 lựa chọn cố định, dùng ChipGroup (trực quan, thay Radio.Group cũ) thay vì
// FieldsFactory schema riêng (create_group_type ở trên giữ lại cho FieldsFactory cũ nếu còn nơi
// khác dùng, không xoá).
const GROUP_TYPE_OPTIONS = [
    {label: 'department', value: 'department'},
    {label: 'choir', value: 'choir'},
]
const STATUS_CHIP_OPTIONS = [StatusTypes.PENDING__1, StatusTypes.NORMAL, StatusTypes.CLOSED]

const validateTitleOrDepartmentGroupTitle = (name, keyName, t) => {
    const maxLength = {
        'title': 120,
        'group_title': 180
    }
    if (keyName !== 'title' && !name) return null;
    const rules = [
        (keyName === 'title') ? required(t) : null,
        lengthBetween(1, maxLength[keyName], t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t),
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return {[keyName]: message}
}

const validateDescription = (description, t) => {
    const rules = [
        lengthBetween(1, 500, t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(description, rules)
    if (message) return {description: message}
}

const validatePatronSaint = (patronSaintName, t) => {
    if (!patronSaintName) return null;
    const rules = [
        lengthBetween(2, 180, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t),
    ].filter(Boolean)
    const message = runRules(patronSaintName, rules)
    if (message) return {patron_saint: message}
}

export const validate = (t) => (values) => {
    const {
        title,
        description,
        group_title,
        patron_saint
    } = values ?? {}
    const errorTitle = validateTitleOrDepartmentGroupTitle(title, 'title', t)
    const errorDepartmentGroupTitle = validateTitleOrDepartmentGroupTitle(group_title, 'group_title', t)
    const errorPatronSaint = validatePatronSaint(patron_saint ? patron_saint.value ?? patron_saint.common_name : undefined, t)
    const errorDescription = validateDescription(description, t)
    return {
        ...errorTitle,
        ...errorDepartmentGroupTitle,
        ...errorPatronSaint,
        ...errorDescription,
    }
}

export const GroupCreateForm = ({
                                     organization,
                                     initialValues,
                                     onSubmit = NullAsDefault,
                                     isSubmitting = false,
                                 }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const _initialValues = useMemo(
        () => ({
            title: undefined,
            description: undefined,
            active: 1,
            status: 1,
            privacy: 1,
            join_policy: 2,
            channel_post_permission: 2,
            group_type: 'department',
            organization,
            organization_id: organization?.id,
            ...(initialValues ?? {}),
        }),
        [organization, initialValues]
    )

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                onSubmit(
                    _.omitBy(
                        {
                            ...values,
                            // Modal này luôn tạo group bên trong 1 organization - gán owner_type/owner_id
                            // để endpoint chung /groups/create (GroupController@create) org-scope + check
                            // quyền admin đúng như OrganizationGroupController trước đây.
                            owner_type: 'organization',
                            owner_id: organization?.id,
                        },
                        _.isUndefined
                    )
                )
            }}>
            {({values, touched, errors, setFieldValue, setValues}) => {
                const onChange = (e) => setFieldValue(e.target.name, e.target.value)
                const isChoirType = values.group_type === 'choir'
                return (
                    <ActionModalWrapper
                        className="gap-3"
                        action={
                            <div className="flex items-center justify-end p-2 background gap-3">
                                <ResetButton disabled={isSubmitting}/>
                                <SubmitButton label="create" dirtyRequired={false} loading={isSubmitting}/>
                            </div>
                        }>
                        <Section theme="highlight">
                            <Field label={t('group type')} required>
                                <ChipGroup
                                    required
                                    options={GROUP_TYPE_OPTIONS}
                                    value={values.group_type}
                                    onChange={(v) => {
                                        setFieldValue('group_type', v)
                                        const fixedTitleKey = FIXED_GROUP_TITLE_BY_TYPE[v]
                                        setFieldValue('group_title', fixedTitleKey ? t(fixedTitleKey) : undefined)
                                    }}
                                />
                            </Field>
                            <Field
                                label={t(isChoirType ? 'choir name' : 'group name')}
                                required
                                error={touched.title && errors.title}>
                                <Input
                                    name="title"
                                    value={values.title}
                                    onChange={onChange}
                                    showCount
                                    maxLength={120}
                                    placeholder={t(isChoirType ? 'choir name placeholder' : 'group name placeholder')}
                                />
                            </Field>
                            <Field label={t('serving in')}>
                                <GroupTitleInput name="group_title" value={values.group_title} onChange={onChange} form={{values}}/>
                            </Field>
                        </Section>

                        <Section icon={SaintsOutlineSvg} title={t('religious details')} subtitle={t('optional')}>
                            <div className={classNames('grid gap-3', isSm ? 'grid-cols-1' : 'grid-cols-2')}>
                                <Field label={t('patron saint')}>
                                    <PatronSaintInput
                                        name="patron_saint"
                                        value={values.patron_saint}
                                        onChange={onChange}
                                        form={{values, setValues}}
                                    />
                                </Field>
                                <Field label={t('patron saint feast day')}>
                                    <PatronSaintFeastDayInput
                                        name="patron_saint_feast_day"
                                        value={values.patron_saint_feast_day}
                                        onChange={onChange}
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section icon={HiOutlineDocumentText} title={t('content')} subtitle={t('optional')}>
                            <Field label={t('description')}>
                                <Input.TextArea
                                    name="description"
                                    value={values.description}
                                    onChange={onChange}
                                    showCount
                                    maxLength={500}
                                    rows={5}
                                    placeholder={t(isChoirType ? 'choir description placeholder' : 'group description placeholder')}
                                />
                            </Field>
                        </Section>
                    </ActionModalWrapper>
                )
            }}
        </Formik>
    )
}

export const GroupUpdateForm = ({
                                     organization,
                                     initialValues,
                                     onSubmit = NullAsDefault,
                                     isSubmitting = false,
                                 }) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const organization_id = getId(organization)
    const isChoir = _.get(initialValues, 'group_type') === 'choir'
    const _initialValues = useMemo(() => {
        const {about, ...rest} = initialValues ?? {}
        return {
            organization_id,
            organization,
            ...rest,
            about: convertFromString(about),
            patron_saint_id: _.get(rest, 'patron_saint.id', ''),
            conductor_id: _.get(rest, 'conductor.id', ''),
        }
    }, [organization_id, organization, initialValues]);

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {
                    about,
                    ...rest
                } = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            about: about ? convertToString(about) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            {({values, touched, errors, setFieldValue, setValues}) => {
                const onChange = (e) => setFieldValue(e.target.name, e.target.value)
                const isChoirType = values.group_type === 'choir'
                return (
                    <ActionModalWrapper
                        className="gap-3"
                        action={
                            <div className="flex items-center justify-end p-2 background gap-3">
                                <ResetButton disabled={isSubmitting}/>
                                <SubmitButton label="save" dirtyRequired={false} loading={isSubmitting}/>
                            </div>
                        }>
                        <Section theme="highlight">
                            <Field
                                label={t(isChoirType ? 'choir name' : 'group name')}
                                required
                                error={touched.title && errors.title}>
                                <Input
                                    name="title"
                                    value={values.title}
                                    onChange={onChange}
                                    showCount
                                    maxLength={120}
                                    placeholder={t(isChoirType ? 'choir name placeholder' : 'group name placeholder')}
                                />
                            </Field>
                            <Field label={t('serving in')}>
                                <GroupTitleInput name="group_title" value={values.group_title} onChange={onChange} form={{values}}/>
                            </Field>
                        </Section>

                        <Section icon={SaintsOutlineSvg} title={t('religious details')} subtitle={t('optional')}>
                            <div className={classNames('grid gap-3', isSm ? 'grid-cols-1' : 'grid-cols-2')}>
                                <Field label={t('patron saint')}>
                                    <PatronSaintInput
                                        name="patron_saint"
                                        value={values.patron_saint}
                                        onChange={onChange}
                                        form={{values, setValues}}
                                    />
                                </Field>
                                <Field label={t('patron saint feast day')}>
                                    <PatronSaintFeastDayInput
                                        name="patron_saint_feast_day"
                                        value={values.patron_saint_feast_day}
                                        onChange={onChange}
                                    />
                                </Field>
                            </div>
                        </Section>

                        <Section icon={HiOutlineDocumentText} title={t('content')} subtitle={t('optional')}>
                            <Field label={t('description')}>
                                <Input.TextArea
                                    name="description"
                                    value={values.description}
                                    onChange={onChange}
                                    showCount
                                    maxLength={500}
                                    rows={5}
                                    placeholder={t(isChoirType ? 'choir description placeholder' : 'group description placeholder')}
                                />
                            </Field>
                            <Field label={t('introduce')}>
                                <AboutInput name="about" value={values.about} onChange={onChange}/>
                            </Field>
                        </Section>

                        <Section icon={BsShieldFillExclamation} title={t('settings')} collapsible defaultCollapsed>
                            <Field label={t('status')} required>
                                <ChipGroup
                                    required
                                    options={STATUS_CHIP_OPTIONS}
                                    value={values.status}
                                    onChange={(v) => setFieldValue('status', v)}
                                />
                            </Field>
                            <Field label={t('active')}>
                                <ActiveToggleInput name="active" value={values.active} onChange={onChange}/>
                            </Field>
                            <div className={classNames('grid gap-3', isSm ? 'grid-cols-1' : 'grid-cols-2')}>
                                <Field label={t('privacy')}>
                                    <PrivacyInput name="privacy" value={values.privacy} onChange={onChange}/>
                                </Field>
                                <Field label={t('join policy')}>
                                    <JoinPolicyInput name="join_policy" value={values.join_policy} onChange={onChange}/>
                                </Field>
                            </div>
                            <ChannelPostPermissionInput name="channel_post_permission" value={values.channel_post_permission} onChange={onChange}/>
                            <PublicToggleInput name="public" value={values.public} onChange={onChange}/>
                            <RequiredSystemAdmin>
                                <PublicByServerToggleInput name="public_by_server" value={values.public_by_server} onChange={onChange}/>
                            </RequiredSystemAdmin>
                        </Section>

                        {isChoir && (
                            <Section icon={PiMusicNotesFill} title={t('choir settings')}>
                                <Field label={t('conductor')}>
                                    <ConductorField name="conductor_id" value={values.conductor_id} onChange={onChange} form={{values}}/>
                                </Field>
                            </Section>
                        )}

                        <Section icon={RiInformationLine} title={t('details')} collapsible defaultCollapsed>
                            <Field label={t('group type')}>
                                <GroupTypeTagInput value={values.group_type}/>
                            </Field>
                            <Field label={t('idname')}>
                                <IdnameInput value={values.idname}/>
                            </Field>
                            <Field label={t('channel')}>
                                <ChannelInput value={values.channel}/>
                            </Field>
                            <Field label={t('join code')}>
                                <JoinCodeInput name="join_code" value={values.join_code} onChange={onChange} form={{values}}/>
                            </Field>
                        </Section>
                    </ActionModalWrapper>
                )
            }}
        </Formik>
    )
}
